import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Calendar, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from "date-fns";
import { useCurrency } from "@/hooks/use-currency";
import { useRealtimeSubscription } from "@/hooks/use-realtime";

export const RevenueTracking = () => {
  const { formatAmount, convertAmount, currency } = useCurrency();
  
  // Enable realtime updates
  useRealtimeSubscription("subscriptions", "revenue-subscriptions");
  useRealtimeSubscription("invoices", "revenue-invoices");
  
  const { data: revenueData, isLoading } = useQuery({
    queryKey: ["revenue-tracking", currency],
    queryFn: async () => {
      const sixMonthsAgo = subMonths(new Date(), 6);
      
      // Fetch subscriptions
      const { data: subscriptions, error: subError } = await supabase
        .from("subscriptions")
        .select("*")
        .gte("created_at", sixMonthsAgo.toISOString())
        .order("created_at", { ascending: true });
      
      if (subError) throw subError;

      // Fetch invoices (exclude draft/cancelled)
      const { data: invoices, error: invError } = await supabase
        .from("invoices")
        .select("*")
        .neq("status", "draft")
        .neq("status", "cancelled")
        .gte("created_at", sixMonthsAgo.toISOString())
        .order("created_at", { ascending: true });
      
      if (invError) throw invError;

      // Group by month
      const monthlyData = new Map();
      const months = eachMonthOfInterval({
        start: sixMonthsAgo,
        end: new Date(),
      });

      months.forEach(month => {
        const key = format(month, "MMM yyyy");
        monthlyData.set(key, { month: key, revenue: 0, count: 0 });
      });

      // Add subscription revenue (exclude invoice-type to avoid double counting)
      subscriptions?.filter(sub => sub.payment_type !== 'invoice').forEach(sub => {
        const key = format(new Date(sub.created_at), "MMM yyyy");
        if (monthlyData.has(key)) {
          const current = monthlyData.get(key);
          const sourceCurrency = (sub.currency as any) || "USD";
          const converted = convertAmount(Number(sub.price), sourceCurrency, currency);
          monthlyData.set(key, {
            ...current,
            revenue: current.revenue + converted,
            count: current.count + 1,
          });
        }
      });

      // Add invoice revenue
      invoices?.forEach(inv => {
        const key = format(new Date(inv.created_at), "MMM yyyy");
        if (monthlyData.has(key)) {
          const current = monthlyData.get(key);
          const sourceCurrency = (inv.currency as any) || "USD";
          const converted = convertAmount(Number(inv.total_amount), sourceCurrency, currency);
          monthlyData.set(key, {
            ...current,
            revenue: current.revenue + converted,
            count: current.count + 1,
          });
        }
      });

      const chartData = Array.from(monthlyData.values());
      
      // Calculate metrics (exclude invoice-type subscriptions to avoid double counting)
      const subscriptionRevenue = subscriptions?.filter(s => s.payment_type !== 'invoice').reduce((sum, s) => {
        const sourceCurrency = (s.currency as any) || "USD";
        return sum + convertAmount(Number(s.price), sourceCurrency, currency);
      }, 0) || 0;
      
      const invoiceRevenue = invoices?.reduce((sum, i) => {
        const sourceCurrency = (i.currency as any) || "USD";
        return sum + convertAmount(Number(i.total_amount), sourceCurrency, currency);
      }, 0) || 0;
      
      const totalRevenue = subscriptionRevenue + invoiceRevenue;
      const avgMonthly = totalRevenue / chartData.length;
      const currentMonth = chartData[chartData.length - 1]?.revenue || 0;
      const previousMonth = chartData[chartData.length - 2]?.revenue || 0;
      const growth = previousMonth ? ((currentMonth - previousMonth) / previousMonth) * 100 : 0;

      return {
        chartData,
        totalRevenue,
        avgMonthly,
        currentMonth,
        growth,
        recordCount: (subscriptions?.filter(s => s.payment_type !== 'invoice').length || 0) + (invoices?.length || 0),
      };
    },
  });

  if (isLoading) {
    return <Card className="p-6">Loading revenue data...</Card>;
  }

  const { chartData = [], totalRevenue = 0, avgMonthly = 0, currentMonth = 0, growth = 0, recordCount = 0 } = revenueData || {};

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <h3 className="text-2xl font-bold mt-2">
                {formatAmount(totalRevenue)}
              </h3>
            </div>
            <div className="p-3 bg-primary/10 rounded-full">
              <DollarSign className="h-6 w-6 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">This Month</p>
              <h3 className="text-2xl font-bold mt-2">
                {formatAmount(currentMonth)}
              </h3>
            </div>
            <div className="p-3 bg-success/10 rounded-full">
              <Calendar className="h-6 w-6 text-success" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Monthly</p>
              <h3 className="text-2xl font-bold mt-2">
                {formatAmount(avgMonthly)}
              </h3>
            </div>
            <div className="p-3 bg-accent/10 rounded-full">
              <TrendingUp className="h-6 w-6 text-accent" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Growth</p>
              <div className="flex items-center gap-2 mt-2">
                <h3 className="text-2xl font-bold">
                  {growth.toFixed(1)}%
                </h3>
                {growth >= 0 ? (
                  <ArrowUpRight className="h-5 w-5 text-success" />
                ) : (
                  <ArrowDownRight className="h-5 w-5 text-destructive" />
                )}
              </div>
            </div>
            <Badge variant={growth >= 0 ? "default" : "destructive"}>
              {growth >= 0 ? "Up" : "Down"}
            </Badge>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Revenue Trend (6 Months)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(262, 83%, 58%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(262, 83%, 58%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="month" className="text-xs" />
            <YAxis className="text-xs" />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number) => [`$${value.toLocaleString()}`, "Revenue"]}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="hsl(262, 83%, 58%)"
              strokeWidth={2}
              fill="url(#revenueGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Monthly Comparison</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="month" className="text-xs" />
            <YAxis className="text-xs" />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number) => [`$${value.toLocaleString()}`, "Revenue"]}
            />
            <Bar dataKey="revenue" fill="hsl(262, 83%, 58%)" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};